import {
  IconButton,
  Typography,
  useTheme,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
} from "@mui/material";
import {
  DarkMode,
  LightMode,
  ArrowBack,
  Person,
  Edit,
  Logout,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setMode, setLogout } from "state";
import { useNavigate, useLocation } from "react-router-dom";
import FlexBetween from "components/FlexBetween";
import { useState } from "react";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const isAuth = Boolean(useSelector((state) => state.token));
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme();
  const dark = theme.palette.neutral.dark;
  const primaryLight = theme.palette.primary.light;
  const alt = theme.palette.background.alt;
  const primary = theme.palette.primary.main;

  const fullName = user ? `${user.firstName} ${user.lastName}` : "";
  const userInitial = user?.firstName?.charAt(0).toUpperCase() || "";

  // Show back button on profile pages and login page, hide on homepage
  const showBackButton = location.pathname !== "/" && (location.pathname.startsWith("/profile") || location.pathname.startsWith("/edit-profile") || location.pathname === "/login");

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditProfile = () => {
    handleMenuClose();
    navigate("/edit-profile");
  };

  const handleLogout = () => {
    handleMenuClose();
    dispatch(setLogout());
  };

  return (
    <FlexBetween padding="1rem 6%" backgroundColor={alt}>
      {/* LEFT - BACK BUTTON (conditional) + LOGO */}
      <FlexBetween gap="0.5rem">
        {showBackButton && (
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack sx={{ fontSize: "25px" }} />
          </IconButton>
        )}
        <FlexBetween
          gap="1rem"
          onClick={() => navigate("/")}
          sx={{
            "&:hover": {
              cursor: "pointer",
            },
          }}
        >
          <img
            src="/logo.png"
            alt="Sociopedia Logo"
            style={{
              height: "30px",
              width: "30px",
              objectFit: "contain"
            }}
          />
          <Typography
            fontWeight="bold"
            fontSize={{xs:"1.1rem", md:"1.3rem"}}
            color="primary"
            sx={{
              "&:hover": {
                color: primaryLight,
              },
            }}
          >
            Sociopedia
          </Typography>
        </FlexBetween>
      </FlexBetween>

      {/* RIGHT - THEME TOGGLE & USER DROPDOWN / LOGIN BUTTON */}
      <FlexBetween gap={{ xs: "0.5rem", md: "1rem" }}>
        <IconButton onClick={() => dispatch(setMode())}>
          {theme.palette.mode === "dark" ? (
            <DarkMode sx={{ fontSize: "25px" }} />
          ) : (
            <LightMode sx={{ color: dark, fontSize: "25px" }} />
          )}
        </IconButton>

        {isAuth ? (
          <>
            <IconButton
              onClick={handleMenuOpen}
              sx={{ p: 0 }}
            >
              <Avatar
                src={user?.picturePath}
                alt={fullName}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: primary,
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                {!user?.picturePath && userInitial}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: { xs: 180, sm: 200 },
                  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : alt,
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                  "& .MuiMenuItem-root": {
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.75 },
                    borderRadius: "8px",
                    mx: { xs: 0.5, sm: 0.75 },
                    my: 0.15,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  },
                  "& .MuiDivider-root": {
                    my: 0.25,
                    mx: { xs: 0.5, sm: 1 },
                  },
                },
              }}
            >
              <MenuItem disabled sx={{ opacity: 1, "&:hover": { backgroundColor: "transparent !important" } }}>
                <Typography fontWeight="700" fontSize="1rem" color={theme.palette.mode === "dark" ? "#fff" : dark}>
                  {fullName}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleMenuClose(); navigate(`/profile/${user._id}`); }}>
                <ListItemIcon>
                  <Person fontSize="small" sx={{ color: primary }} />
                </ListItemIcon>
                My Profile
              </MenuItem>
              <MenuItem onClick={handleEditProfile}>
                <ListItemIcon>
                  <Edit fontSize="small" sx={{ color: primary }} />
                </ListItemIcon>
                Edit Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: primary }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button
            onClick={() => navigate("/login")}
            sx={{
              color: theme.palette.background.alt,
              backgroundColor: theme.palette.primary.main,
              borderRadius: "3rem",
              padding: "0.3rem 1rem",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            }}
          >
            Login
          </Button>
        )}
      </FlexBetween>
    </FlexBetween>
  );
};

export default Navbar;